import * as React from 'react';
import { Component, ErrorInfo, ReactNode } from 'react';
import { Typography } from '@material-ui/core';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

// Keeps a render crash inside the page area instead of unmounting the whole app.
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(error, errorInfo.componentStack);
  }

  // [EN] Reset only when an error is shown. Remounting via `key` instead would tear down
  //      healthy subtrees on every route-based tab switch and drop unsaved edits.
  // [KO] 에러가 떠 있을 때만 리셋한다. `key` 로 리마운트하면 경로 기반 탭을 옮길 때마다
  //      멀쩡한 하위 트리까지 다시 만들어져 저장 안 한 수정 내용이 사라진다.
  componentDidUpdate(prevProps: Props): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Wrapper>
          <Typography variant="h5" gutterBottom>
            Something went wrong while displaying this page.
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Please refresh the page or try again later.
          </Typography>
        </Wrapper>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

const Wrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(10)}px ${({ theme }) => theme.spacing(6)}px;
  text-align: center;
`;
